import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Tag, Space, message, Popconfirm, Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { fetchDevices, registerDevice, revokeDevice } from '../../api';
import { getDeviceInfo } from '../../../../services/deviceFingerprint';

const reportResult = (res, okMsg) => {
  if (res?.pendingApproval) message.info('Submitted for approval — the device will be trusted once approved.');
  else message.success(okMsg);
};

const RegisteredDevicesTab = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try { setData((await fetchDevices()).data || []); }
    catch { message.error('Failed to load devices'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openRegister = () => { form.resetFields(); setModalOpen(true); };

  // Convenience: fill the form with THIS browser's fingerprint.
  const useThisDevice = async () => {
    const d = await getDeviceInfo();
    if (!d.deviceFingerprint) return message.warning('Could not read this device fingerprint.');
    form.setFieldsValue({
      browserFingerprint: d.deviceFingerprint,
      deviceName: d.deviceName,
      operatingSystem: d.operatingSystem,
      browser: d.browser,
    });
    message.success('Filled with this device fingerprint');
  };

  const submit = async () => {
    const values = await form.validateFields();
    try { reportResult(await registerDevice(values), 'Device registered'); setModalOpen(false); load(); }
    catch (e) { message.error(e?.response?.data?.message || 'Registration failed'); }
  };

  const onRevoke = async (rec) => {
    try { reportResult(await revokeDevice(rec._id), 'Device revoked'); load(); }
    catch (e) { message.error(e?.response?.data?.message || 'Revoke failed'); }
  };

  const columns = [
    { title: 'Code', dataIndex: 'deviceCode' },
    { title: 'Name', dataIndex: 'deviceName' },
    { title: 'OS', dataIndex: 'operatingSystem' },
    { title: 'Browser', dataIndex: 'browser' },
    { title: 'Fingerprint', dataIndex: 'browserFingerprint', render: (f) => <code>{(f || '').slice(0, 12)}…</code> },
    { title: 'Status', dataIndex: 'status', render: (s) => <Tag color={s === 'ACTIVE' ? 'green' : 'red'}>{s}</Tag> },
    { title: 'Last Seen', dataIndex: 'lastSeen', render: (d) => (d ? new Date(d).toLocaleString() : '—') },
    {
      title: 'Actions', render: (_, rec) => rec.status === 'ACTIVE' && (
        <Popconfirm title="Revoke this device?" onConfirm={() => onRevoke(rec)}><a>Revoke</a></Popconfirm>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={openRegister} style={{ marginBottom: 16 }}>
        Register Device
      </Button>
      <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" />

      <Modal title="Register Device" open={modalOpen} onOk={submit} onCancel={() => setModalOpen(false)} destroyOnClose>
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message="The browser fingerprint is the trust key. MAC address is optional and never trusted alone." />
        <Button onClick={useThisDevice} style={{ marginBottom: 12 }}>Use this device's fingerprint</Button>
        <Form form={form} layout="vertical">
          <Form.Item name="browserFingerprint" label="Browser Fingerprint" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="deviceName" label="Device Name"><Input /></Form.Item>
          <Form.Item name="hostname" label="Hostname"><Input /></Form.Item>
          <Form.Item name="operatingSystem" label="Operating System"><Input /></Form.Item>
          <Form.Item name="browser" label="Browser"><Input /></Form.Item>
          <Form.Item name="serialNumber" label="Serial Number"><Input /></Form.Item>
          <Form.Item name="macAddress" label="MAC Address (optional)"><Input /></Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default RegisteredDevicesTab;
